import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import { relatedLandingPages } from "@/data/relatedLandingPages.data";
import { landingPages } from "@/data/landingPages.data";

interface RelatedPagesSectionProps {
  currentPath: string;
}

// Pages registered in src/data/landingPages (rendered by <LandingPage>) don't
// need a hand-written entry in relatedLandingPages.data.ts: if none exists,
// fall back to up to 3 other registered pages sharing the same `cluster`,
// using each page's own hero title as the anchor text.
function getFallbackRelatedPages(currentPath: string): { path: string; title: string }[] {
  const currentSlug = currentPath.replace(/^\//, "");
  const current = landingPages[currentSlug];
  if (!current) return [];

  return Object.values(landingPages)
    .filter((page) => page.slug !== currentSlug && page.cluster === current.cluster)
    .slice(0, 3)
    .map((page) => ({ path: `/${page.slug}`, title: page.heroSection.title }));
}

export default function RelatedPagesSection({
  currentPath,
}: RelatedPagesSectionProps) {
  const related = relatedLandingPages[currentPath] ?? getFallbackRelatedPages(currentPath);
  if (!related?.length) return null;

  return (
    <section className="pb-16 pt-4 sm:pb-24 sm:pt-8">
      <div className="scontainer">
        <h2 className="mb-6 text-center text-2xl font-semibold sm:text-3xl">
          Explore More Ways Fydo Helps You Grow
        </h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {related.map((page) => (
            <Link
              key={page.path}
              href={page.path}
              className="group flex items-center justify-between gap-3 rounded-xl border border-border bg-white p-5 transition-colors hover:border-primary"
            >
              <span className="font-medium text-secondary">{page.title}</span>
              <ArrowUpRight className="size-5 shrink-0 text-muted-2-foreground transition-colors group-hover:text-primary" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
